import axios, { AxiosError, AxiosResponse } from "axios";
import { logoutAtendente, TOKEN_KEY } from "../services/AuthService"

export const API_URL = (import.meta.env.REACT_APP_API_URL || window.location.origin).replace(/\/$/, "");

const api = axios.create({
  baseURL: API_URL,
  responseType: "json",
  timeout: 3600000,
  timeoutErrorMessage: "A solicitação que fez demorou muito. Por favor tente novamente.",
  headers: {
    "Content-Type": "application/json",
    "X-Request-Client": "Portal",
  },
});

export default api;

interface ServerError {
  error: string;
}

type ErrorPath = "/500";

const errorRoutes: { [status: number]: ErrorPath } = {
  500: "/500",
};

function redirectTo(path: ErrorPath) {
  if (window.location.pathname !== path) {
    window.location.replace(path);
  }
}

function unauthorizedRedirect(pathname: string) {
  if (pathname === "/admin" || pathname === "/admin/") {
    return "/admin/login";
  }

  return pathname.startsWith("/admin/") ? "/404" : "/login";
}

export function isServerError(error: ServerError | unknown): error is ServerError {
  if (typeof error === "object" && error !== null && error.hasOwnProperty("error")) {
    // tslint:disable-next-line: strict-type-predicates
    return (error as ServerError).error !== undefined;
  }

  return false;
}

function storeRotatedToken(headers?: { [key: string]: unknown }) {
  const token = headers && headers[TOKEN_KEY];

  if (typeof token === "string" && token !== "") {
    localStorage.setItem(TOKEN_KEY, token);
  }
}

api.interceptors.response.use(function (response) {
  storeRotatedToken(response.headers);
  return response;
}, function (error: AxiosError) {
  storeRotatedToken(error.response?.headers);
  const responseStatus = error.response?.status;
  const status = responseStatus ?? (error.code === "ECONNABORTED" ? 408 : undefined);

  const isSignIn = (error.config?.url ?? "").endsWith("/auth/sign_in");

  if (status === 401 && !isSignIn) {
    logoutAtendente();
    window.location.href = unauthorizedRedirect(window.location.pathname);
    return Promise.reject(error);
  }

  if (status && errorRoutes[status]) {
    redirectTo(errorRoutes[status]);
  }

  return Promise.reject(error);
});

export async function get<I>(url: string, params = {}) {
  return api
    .get<I>(url, { params })
    .then((response: AxiosResponse<I>) => response.data)
    .catch((error: AxiosError<ServerError>) => {
      if (error.response) { throw error.response.data; }
      throw error;
    });
}

export async function post<I>(url: string, params = {}, config = {}) {
  return api
    .post<I>(url, params, config)
    .then((response: AxiosResponse<I>) => response.data)
    .catch((error: AxiosError<ServerError>) => {
      if (error.response) { throw error.response.data; }
      throw error;
    });
}

export async function postCustomize<I>(url: string, params = {}, config = {}) {
  return api
    .post<I>(url, params, config)
    .then((response: AxiosResponse<I>) => response)
    .catch((error: AxiosError<ServerError>) => {
      if (error.response) { throw error.response.data; }
      throw error;
    });
}

export async function auth<I>(url: string, params = {}, config = {}) {
  return api
    .post<I>(url, params, config)
    .then((response: AxiosResponse<I>) => response)
    .catch((error: AxiosError<ServerError>) => {
      if (error.response) { throw error.response.data; }
      throw error;
    });
}

export async function getWithConfig<I>(url: string, config = {}) {
  return api
    .get<I>(url, config)
    .then((response: AxiosResponse<I>) => response)
    .catch((error: AxiosError<ServerError>) => {
      if (error.response) { throw error.response.data; }
      throw error;
    });
}

export async function put<I>(url: string, params = {}, config = {}) {
  return api
    .put<I>(url, params, config)
    .then((response: AxiosResponse<I>) => response)
    .catch((error: AxiosError<ServerError>) => {
      if (error.response) { throw error.response.data; }
      throw error;
    });
}

export async function destroy<I>(url: string, params = {}) {
  return api
    .delete<I>(url, params)
    .then((response: AxiosResponse<I>) => response)
    .catch((error: AxiosError<ServerError>) => {
      if (error.response) { throw error.response.data; }
      throw error;
    });
}
